import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CreditCard, 
  Eye, 
  ShoppingBag, 
  ExternalLink,
  Plus,
  Settings,
  Users,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  TrendingUp,
  Star,
  BarChart3,
  Loader2,
  Link as LinkIcon,
  Download,
  Crown,
  ArrowLeft,
  Monitor,
  Smartphone,
  Globe,
  Clock,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 }
};

export default function OwnerDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [cards, setCards] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountStatus, setAccountStatus] = useState(null);

  // Pagination & sorting state for profiles
  const [profilePage, setProfilePage] = useState(1);
  const [profileTotalPages, setProfileTotalPages] = useState(1);
  const [profileTotal, setProfileTotal] = useState(0);
  const [profileSort, setProfileSort] = useState('newest');
  const [profileSearch, setProfileSearch] = useState('');

  // Pagination & sorting state for cards
  const [cardPage, setCardPage] = useState(1);
  const [cardTotalPages, setCardTotalPages] = useState(1);
  const [cardTotal, setCardTotal] = useState(0);
  const [cardSort, setCardSort] = useState('newest');
  const [cardSearch, setCardSearch] = useState('');

  useEffect(() => {
    checkAccountAndFetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAccountAndFetchData = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // First check account status
      const statusRes = await fetch(`${API}/owner/account-status`, { headers });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setAccountStatus(statusData);
        
        // If account is pending, redirect to pending page
        if (!statusData.is_active) {
          navigate('/account-pending');
          return;
        }
      }

      // Account is active, fetch dashboard data
      fetchData();
    } catch (error) {
      console.error('Error checking account status:', error);
      setLoading(false);
    }
  };

  const fetchData = async (pPage, pSort, pSearch, cPage, cSort, cSearch) => {
    setLoading(true);
    const pp = pPage ?? profilePage;
    const ps = pSort ?? profileSort;
    const pq = pSearch ?? profileSearch;
    const cp = cPage ?? cardPage;
    const cs = cSort ?? cardSort;
    const cq = cSearch ?? cardSearch;
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [profilesRes, cardsRes, ordersRes, subsRes, analyticsRes] = await Promise.all([
        fetch(`${API}/owner/profiles?page=${pp}&limit=10&sort=${ps}&search=${encodeURIComponent(pq)}`, { headers }),
        fetch(`${API}/owner/cards?page=${cp}&limit=20&sort=${cs}&search=${encodeURIComponent(cq)}`, { headers }),
        fetch(`${API}/owner/orders`, { headers }),
        fetch(`${API}/owner/subscriptions`, { headers }),
        fetch(`${API}/owner/analytics`, { headers })
      ]);

      if (profilesRes.ok) {
        const data = await profilesRes.json();
        setProfiles(data.profiles || []);
        setProfileTotal(data.total || 0);
        setProfileTotalPages(data.total_pages || 1);
        setProfilePage(data.page || 1);
      }

      if (cardsRes.ok) {
        const data = await cardsRes.json();
        setCards(data.cards || []);
        setCardTotal(data.total || 0);
        setCardTotalPages(data.total_pages || 1);
        setCardPage(data.page || 1);
      }

      if (ordersRes.ok) {
        const data = await ordersRes.json();
        setOrders(data);
      }

      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscriptionStatus(data);
      }

      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
        
        // Fetch visitors for the first profile with views
        if (data.profiles?.length > 0) {
          const topProfile = data.profiles[0].profile_id;
          try {
            const visitorsRes = await fetch(`${API}/owner/visitors/${topProfile}`, { headers });
            if (visitorsRes.ok) {
              setVisitors(await visitorsRes.json());
            }
          } catch (e) { console.error('Failed to fetch analytics:', e); }
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProfile = async (profileId) => {
    try {
      const response = await fetch(`${API}/owner/profiles/${profileId}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
      });

      if (response.ok) {
        const data = await response.json();
        setProfiles(profiles.map(p => 
          p.profile_id === profileId ? { ...p, is_public: data.is_public } : p
        ));
        toast.success(data.message);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  // (G) Generate secure edit link for profile
  const handleRequestEditLink = async (profileId) => {
    try {
      const response = await fetch(`${API}/owner/profiles/${profileId}/request-edit-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
      });

      if (response.ok) {
        const data = await response.json();
        const editUrl = `${window.location.origin}${data.edit_url}`;
        
        // Copy to clipboard
        navigator.clipboard.writeText(editUrl);
        toast.success(t('owner.editLinkCopied'), {
          description: t('owner.editLinkExpires', { date: new Date(data.expires_at).toLocaleDateString('fr-FR') })
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  // (C) Download invoice for an order
  const handleDownloadInvoice = async (orderId) => {
    try {
      const invoiceUrl = `${API}/invoices/${orderId}?token=${token}`;
      window.open(invoiceUrl, '_blank');
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const activeProfiles = profiles.filter(p => p.subscription_active && p.is_active);
  const expiredProfiles = profiles.filter(p => !p.subscription_active);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div {...fadeUp} className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                {t('owner.dashboard')}
              </h1>
              <p className="text-slate-600">
                {t('owner.welcomeBack')}, {user?.first_name || user?.email}!
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/">
                <Button variant="outline" className="rounded-xl gap-2" data-testid="back-to-site-btn">
                  <ArrowLeft className="w-4 h-4" />
                  {t('owner.backToSite') || 'Retour au site'}
                </Button>
              </Link>
              <Link to="/owner/settings">
                <Button variant="outline" className="rounded-xl gap-2" data-testid="settings-btn">
                  <Settings className="w-4 h-4" />
                  {t('dashboard.settings')}
                </Button>
              </Link>
              <Link to="/owner/buy-cards">
                <Button className="btn-primary rounded-xl gap-2" data-testid="buy-more-btn">
                  <Plus className="w-4 h-4" />
                  {t('owner.buyMoreCards')}
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Active Plan Banner (F) */}
        <motion.div {...fadeUp} className="mb-8">
          <div className={`rounded-2xl p-5 ${user?.account_type === 'business' ? 'bg-gradient-to-r from-purple-500 to-indigo-600' : 'bg-gradient-to-r from-blue-500 to-cyan-600'} text-white`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Crown className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">{t('owner.currentPlan')}</p>
                  <h2 className="font-heading text-2xl font-bold">
                    {user?.account_type?.toUpperCase() || 'PRO'}
                  </h2>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-white/80 text-sm">{t('owner.cardsLimit')}</p>
                  <p className="font-bold text-lg">
                    {cardTotal} / {user?.account_type === 'business' ? '1000' : '10'}
                  </p>
                </div>
                {subscriptionStatus?.active_profiles?.[0]?.subscription_expires && (
                  <div className="text-right">
                    <p className="text-white/80 text-sm">{t('owner.expiresOn')}</p>
                    <p className="font-bold text-lg">
                      {new Date(subscriptionStatus.active_profiles[0].subscription_expires).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Account Info Banner (Business Only) */}
        {user?.account_type === 'business' && (
          <motion.div {...fadeUp} className="mb-8">
            <div className={`rounded-2xl p-5 ${user?.sigle_validated ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${user?.sigle_validated ? 'bg-purple-100' : 'bg-yellow-100'}`}>
                  {user?.sigle_validated ? (
                    <CheckCircle className="w-6 h-6 text-purple-600" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-yellow-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-slate-900">
                      {t('owner.businessAccount')}
                    </p>
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-mono font-bold">
                      {user?.business_sigle}
                    </span>
                  </div>
                  <p className={`text-sm mb-2 ${user?.sigle_validated ? 'text-[#00C48C]' : 'text-yellow-600'}`}>
                    {user?.sigle_validated ? t('owner.sigleValidated') : t('owner.siglePending')}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t('owner.businessExplanation')}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {t('owner.profileUrlFormat')}: <span className="font-mono">{user?.business_sigle}0001, {user?.business_sigle}0002...</span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <motion.div {...fadeUp} className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-profiles">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[#0A2540]/10 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-[#0A2540]" />
              </div>
              <span className="text-sm text-slate-500">{t('owner.totalProfiles')}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{profileTotal}</p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-active">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[#00C48C]/10 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[#00C48C]" />
              </div>
              <span className="text-sm text-slate-500">{t('owner.activeProfiles')}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{activeProfiles.length}</p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.15 }} className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-views">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Eye className="w-5 h-5 text-indigo-600" />
              </div>
              <span className="text-sm text-slate-500">{t('owner.totalViews')}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{analytics?.total_views || 0}</p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-cards">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-slate-500">{t('owner.totalCards')}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{cardTotal}</p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.3 }} className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-orders">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm text-slate-500">{t('owner.totalOrders')}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{orders.length}</p>
          </motion.div>
        </div>

        {/* Profile Analytics Section */}
        {analytics && analytics.total_views > 0 && (
          <motion.div {...fadeUp} className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-soft mb-8 text-white overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold">
                    {t('owner.profileAnalytics')}
                  </h2>
                </div>
                {analytics.popular_profiles > 0 && (
                  <span className="px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-sm font-medium flex items-center gap-1">
                    <Star className="w-4 h-4" />
                    {analytics.popular_profiles} Popular
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold">{analytics.total_views}</p>
                  <p className="text-sm text-white/80">{t('owner.totalViews')}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold">{analytics.total_profiles}</p>
                  <p className="text-sm text-white/80">{t('owner.totalProfiles')}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold">
                    {analytics.total_profiles > 0 ? Math.round(analytics.total_views / analytics.total_profiles) : 0}
                  </p>
                  <p className="text-sm text-white/80">{t('owner.avgViewsPerProfile')}</p>
                </div>
              </div>

              {/* Top Profiles */}
              {analytics.profiles?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-white/80 mb-2">{t('owner.topProfiles')}</p>
                  <div className="flex gap-2 flex-wrap">
                    {analytics.profiles.slice(0, 3).map((p, idx) => (
                      <div key={p.profile_id} className="bg-white/10 rounded-lg px-3 py-2 flex items-center gap-2">
                        <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-sm font-medium">{p.profile_id}</span>
                        <span className="text-xs text-white/70">{p.view_count} {t('owner.views')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Recent Visitors Section */}
        {visitors && visitors.recent_visitors?.length > 0 && (
          <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft mb-8" data-testid="visitors-section">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-slate-900">
                      {t('owner.recentVisitors') || 'Visiteurs'}
                    </h2>
                    {visitors.today_views > 0 && (
                      <p className="text-sm text-emerald-600 font-medium">
                        {visitors.today_views} {t('owner.viewsToday') || "vue(s) aujourd'hui"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="divide-y divide-slate-50 max-h-[400px] overflow-y-auto">
              {visitors.recent_visitors.slice(0, 8).map((v, i) => {
                const viewDate = new Date(v.viewed_at);
                const now = new Date();
                const diffMs = now - viewDate;
                const diffMin = Math.floor(diffMs / 60000);
                const diffHrs = Math.floor(diffMs / 3600000);
                const diffDays = Math.floor(diffMs / 86400000);
                let timeAgo;
                if (diffMin < 1) timeAgo = "A l'instant";
                else if (diffMin < 60) timeAgo = `${diffMin}min`;
                else if (diffHrs < 24) timeAgo = `${diffHrs}h`;
                else timeAgo = `${diffDays}j`;

                return (
                  <div key={i} className="px-6 py-3 flex items-center justify-between" data-testid={`visitor-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                        v.device === 'mobile' ? 'bg-blue-50' : 'bg-slate-50'
                      }`}>
                        {v.device === 'mobile' 
                          ? <Smartphone className="w-4 h-4 text-blue-500" /> 
                          : <Monitor className="w-4 h-4 text-slate-500" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">
                          {v.device === 'mobile' ? 'Mobile' : 'Desktop'} &middot; {v.browser || 'Unknown'}
                        </p>
                        <p className="text-xs text-slate-400">
                          {v.referrer ? `via ${new URL(v.referrer).hostname}` : 'Visite directe'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      {timeAgo}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Profiles Section */}
        <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft mb-8">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-heading text-xl font-semibold text-slate-900">
                {t('owner.myProfiles')} <span className="text-sm font-normal text-slate-400">({profileTotal})</span>
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder={t('common.search') || 'Rechercher...'}
                    value={profileSearch}
                    onChange={(e) => { setProfileSearch(e.target.value); setProfilePage(1); }}
                    onKeyDown={(e) => e.key === 'Enter' && fetchData(1, profileSort, profileSearch)}
                    className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 w-40"
                    data-testid="profile-search-input"
                  />
                </div>
                <select
                  value={profileSort}
                  onChange={(e) => { setProfileSort(e.target.value); fetchData(1, e.target.value, profileSearch); }}
                  className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 bg-white"
                  data-testid="profile-sort-select"
                >
                  <option value="newest">{t('owner.sortNewest') || 'Plus récent'}</option>
                  <option value="oldest">{t('owner.sortOldest') || 'Plus ancien'}</option>
                  <option value="views">{t('owner.sortMostViewed') || 'Plus consulté'}</option>
                  <option value="sigle">{t('owner.sortSigle') || 'Sigle (numérique)'}</option>
                </select>
                <Button variant="ghost" onClick={() => fetchData()} className="gap-2" data-testid="refresh-btn" disabled={loading}>
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  {t('common.refresh')}
                </Button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {profiles.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">{t('owner.noProfiles')}</p>
                <Link to="/owner/buy-cards">
                  <Button className="btn-primary rounded-xl gap-2">
                    <Plus className="w-4 h-4" />
                    {t('owner.getStarted')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
                {profiles.map((profile) => (
                  <div 
                    key={profile.profile_id} 
                    className={`border rounded-xl p-4 transition-all ${
                      profile.subscription_active && profile.is_active 
                        ? 'border-slate-200 hover:border-slate-300' 
                        : 'border-red-200 bg-red-50/50'
                    }`}
                    data-testid={`profile-${profile.profile_id}`}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        {profile.photo ? (
                          <img 
                            src={profile.photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo}` : profile.photo} 
                            alt="" 
                            className="w-14 h-14 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center">
                            <span className="text-xl font-bold text-slate-400">
                              {profile.first_name?.[0] || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">
                              {profile.first_name} {profile.last_name}
                            </p>
                            {/* Popular Badge */}
                            {analytics?.profiles?.find(p => p.profile_id === profile.profile_id)?.is_popular && (
                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full flex items-center gap-1">
                                <Star className="w-3 h-3" />
                                Popular
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{profile.job_title || t('owner.noJobTitle')}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-xs text-[#0A2540] font-mono">
                              {profile.profile_id}
                            </p>
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {analytics?.profiles?.find(p => p.profile_id === profile.profile_id)?.view_count || 0} {t('owner.views')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {/* Status Badges */}
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            profile.subscription_active 
                              ? 'bg-[#00C48C]/10 text-[#00A67E]' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {profile.subscription_active ? t('owner.subscriptionActive') : t('owner.subscriptionExpired')}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            profile.is_public 
                              ? 'bg-[#0A2540]/10 text-[#0A2540]' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {profile.is_public ? t('owner.profileVisible') : t('owner.profileHidden')}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Link to={`/owner/profile/${profile.profile_id}`}>
                            <Button variant="outline" size="sm" className="rounded-lg" data-testid={`edit-${profile.profile_id}`}>
                              <Settings className="w-4 h-4" />
                            </Button>
                          </Link>
                          <a 
                            href={`/user/${profile.profile_id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <Button variant="outline" size="sm" className="rounded-lg" data-testid={`view-${profile.profile_id}`}>
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                          {/* (G) Generate secure edit link button */}
                          <Button 
                            variant="outline"
                            size="sm" 
                            className="rounded-lg"
                            onClick={() => handleRequestEditLink(profile.profile_id)}
                            title={t('owner.generateEditLink')}
                            data-testid={`edit-link-${profile.profile_id}`}
                          >
                            <LinkIcon className="w-4 h-4" />
                          </Button>
                          {/* Visibility toggle - disabled if toggle not enabled */}
                          <Button 
                            variant={profile.is_public ? "outline" : "default"}
                            size="sm" 
                            className={`rounded-lg ${!profile.visibility_toggle_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={() => profile.visibility_toggle_enabled && handleToggleProfile(profile.profile_id)}
                            disabled={!profile.visibility_toggle_enabled}
                            title={!profile.visibility_toggle_enabled ? t('owner.toggleDisabledPending') : (profile.is_public ? t('owner.hideProfile') : t('owner.showProfile'))}
                            data-testid={`toggle-${profile.profile_id}`}
                          >
                            {profile.is_public ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Expiry Warning */}
                    {!profile.subscription_active && (
                      <div className="mt-4 p-3 bg-red-100 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          <span className="text-sm text-red-700">{t('owner.renewToActivate')}</span>
                        </div>
                        <Link to="/owner/subscriptions">
                          <Button size="sm" variant="destructive" className="rounded-lg">
                            {t('owner.renew')}
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Profiles Pagination */}
            {profileTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100" data-testid="profile-pagination">
                <p className="text-sm text-slate-500">
                  Page {profilePage} / {profileTotalPages} ({profileTotal} {t('owner.totalProfiles') || 'profils'})
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    disabled={profilePage <= 1}
                    onClick={() => { setProfilePage(profilePage - 1); fetchData(profilePage - 1, profileSort, profileSearch); }}
                    data-testid="profile-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: Math.min(5, profileTotalPages) }, (_, i) => {
                    let pageNum;
                    if (profileTotalPages <= 5) pageNum = i + 1;
                    else if (profilePage <= 3) pageNum = i + 1;
                    else if (profilePage >= profileTotalPages - 2) pageNum = profileTotalPages - 4 + i;
                    else pageNum = profilePage - 2 + i;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === profilePage ? 'default' : 'outline'}
                        size="sm"
                        className="rounded-lg w-8 h-8 p-0"
                        onClick={() => { setProfilePage(pageNum); fetchData(pageNum, profileSort, profileSearch); }}
                        data-testid={`profile-page-${pageNum}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    disabled={profilePage >= profileTotalPages}
                    onClick={() => { setProfilePage(profilePage + 1); fetchData(profilePage + 1, profileSort, profileSearch); }}
                    data-testid="profile-next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Cards Section */}
        <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft mb-8" data-testid="cards-section">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-heading text-xl font-semibold text-slate-900">
                {t('owner.myCards') || 'Mes Cartes'} <span className="text-sm font-normal text-slate-400">({cardTotal})</span>
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder={t('common.search') || 'Rechercher...'}
                    value={cardSearch}
                    onChange={(e) => { setCardSearch(e.target.value); setCardPage(1); }}
                    onKeyDown={(e) => e.key === 'Enter' && fetchData(profilePage, profileSort, profileSearch, 1, cardSort, cardSearch)}
                    className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 w-40"
                    data-testid="card-search-input"
                  />
                </div>
                <select
                  value={cardSort}
                  onChange={(e) => { setCardSort(e.target.value); fetchData(profilePage, profileSort, profileSearch, 1, e.target.value, cardSearch); }}
                  className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 bg-white"
                  data-testid="card-sort-select"
                >
                  <option value="newest">{t('owner.sortNewest') || 'Plus récent'}</option>
                  <option value="oldest">{t('owner.sortOldest') || 'Plus ancien'}</option>
                  <option value="views">{t('owner.sortMostViewed') || 'Plus consulté'}</option>
                  <option value="sigle">{t('owner.sortSigle') || 'Sigle (numérique)'}</option>
                </select>
              </div>
            </div>
          </div>
          <div className="p-6">
            {cards.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">{t('owner.noCards') || 'Aucune carte'}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                {cards.map((card) => (
                  <div
                    key={card.card_id}
                    className="flex items-center justify-between p-3 border border-slate-100 rounded-xl hover:border-slate-200 transition-colors"
                    data-testid={`card-${card.card_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        card.color === 'black' ? 'bg-slate-900' : card.color === 'white' ? 'bg-slate-100 border border-slate-200' : 'bg-gradient-to-br from-purple-500 to-indigo-600'
                      }`}>
                        <CreditCard className={`w-5 h-5 ${card.color === 'black' ? 'text-white' : card.color === 'white' ? 'text-slate-600' : 'text-white'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-sm font-medium text-slate-900">{card.profile_id}</p>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            card.status === 'active' ? 'bg-[#00C48C]/10 text-[#00A67E]' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {card.status}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">
                          {card.serial_number} &middot; {card.product_type} {card.color ? `(${card.color})` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-xs text-slate-400">
                        {card.created_at ? new Date(card.created_at).toLocaleDateString('fr-FR') : ''}
                      </p>
                      <a href={`/user/${card.profile_id}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="rounded-lg" data-testid={`view-card-${card.card_id}`}>
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cards Pagination */}
            {cardTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100" data-testid="card-pagination">
                <p className="text-sm text-slate-500">
                  Page {cardPage} / {cardTotalPages} ({cardTotal} {t('owner.totalCards') || 'cartes'})
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    disabled={cardPage <= 1}
                    onClick={() => { setCardPage(cardPage - 1); fetchData(profilePage, profileSort, profileSearch, cardPage - 1, cardSort, cardSearch); }}
                    data-testid="card-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: Math.min(5, cardTotalPages) }, (_, i) => {
                    let pageNum;
                    if (cardTotalPages <= 5) pageNum = i + 1;
                    else if (cardPage <= 3) pageNum = i + 1;
                    else if (cardPage >= cardTotalPages - 2) pageNum = cardTotalPages - 4 + i;
                    else pageNum = cardPage - 2 + i;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === cardPage ? 'default' : 'outline'}
                        size="sm"
                        className="rounded-lg w-8 h-8 p-0"
                        onClick={() => { setCardPage(pageNum); fetchData(profilePage, profileSort, profileSearch, pageNum, cardSort, cardSearch); }}
                        data-testid={`card-page-${pageNum}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    disabled={cardPage >= cardTotalPages}
                    onClick={() => { setCardPage(cardPage + 1); fetchData(profilePage, profileSort, profileSearch, cardPage + 1, cardSort, cardSearch); }}
                    data-testid="card-next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
        {subscriptionStatus && (
          <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft mb-8">
            <div className="p-6 border-b border-slate-100">
              <h2 className="font-heading text-xl font-semibold text-slate-900">
                {t('owner.subscriptionSummary')}
              </h2>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className="text-3xl font-bold text-slate-900">{subscriptionStatus.total_profiles}</p>
                  <p className="text-sm text-slate-500">{t('owner.totalProfiles')}</p>
                </div>
                <div className="text-center p-4 bg-[#00C48C]/5 rounded-xl">
                  <p className="text-3xl font-bold text-[#00C48C]">{subscriptionStatus.active_profiles?.length || 0}</p>
                  <p className="text-sm text-slate-500">{t('owner.activeSubscriptions')}</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <p className="text-3xl font-bold text-red-600">{subscriptionStatus.expired_profiles?.length || 0}</p>
                  <p className="text-sm text-slate-500">{t('owner.expiredSubscriptions')}</p>
                </div>
              </div>

              {expiredProfiles.length > 0 && (
                <div className="mt-6 text-center">
                  <p className="text-slate-600 mb-4">
                    {t('owner.renewalCost')}: <span className="font-bold">${subscriptionStatus.renewal_cost_per_profile}/profile/{t('owner.year')}</span>
                  </p>
                  <Link to="/owner/subscriptions">
                    <Button className="btn-primary rounded-xl" data-testid="manage-subscriptions-btn">
                      {t('owner.manageSubscriptions')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Recent Orders */}
        <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-slate-900">
                {t('owner.recentOrders')}
              </h2>
              <Link to="/owner/orders" className="text-[#0A2540] text-sm hover:text-[#0A2540]">
                {t('common.viewAll')}
              </Link>
            </div>
          </div>
          <div className="p-6">
            {orders.length === 0 ? (
              <p className="text-center text-slate-500 py-8">{t('owner.noOrders')}</p>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                {orders.slice(0, 5).map((order) => (
                  <div key={order.order_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                        <ShoppingBag className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{order.order_id}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium">${order.total}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          order.status === 'paid' 
                            ? 'bg-[#00C48C]/10 text-[#00A67E]' 
                            : order.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                      {/* (C) Invoice Download Button */}
                      {order.status === 'paid' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadInvoice(order.order_id)}
                          className="rounded-lg"
                          title={t('checkout.downloadInvoice')}
                          data-testid={`download-invoice-${order.order_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
